<template>
    <section class="student-dashboard">
        <div class="container student-dashboard__container">
            <StudentSidebar :student="student" />

            <main class="student-dashboard__main">
                <header class="student-dashboard__topbar">
                    <div>
                        <span class="student-dashboard__eyebrow">{{ todayLabel }}</span>
                        <h1>Dashboard</h1>
                    </div>
                    <div class="student-dashboard__actions">
                        <!-- <button type="button" class="student-dashboard__icon-btn" aria-label="Notifications">
                            <i class="ri-notification-3-line"></i>
                        </button> -->
                        <a :href="firstCourseUrl" class="student-dashboard__primary-action">
                            Continue Learning
                        </a>
                    </div>
                </header>

                <section class="student-dashboard__welcome">
                    <div>
                        <span class="student-dashboard__eyebrow">Learning overview</span>
                        <h2>Welcome back, {{ fullName }}</h2>
                        <p>
                            Your dashboard keeps every enrolled course, average completion, and
                            next learning step in one cleaner view.
                        </p>
                    </div>
                    <div class="student-dashboard__welcome-highlights">
                        <article v-for="highlight in welcomeHighlights" :key="highlight.label">
                            <strong>{{ highlight.value }}</strong>
                            <small>{{ highlight.label }}</small>
                        </article>
                    </div>
                </section>

                <section class="student-dashboard__stats" aria-label="Learning summary">
                    <article
                        v-for="stat in stats"
                        :key="stat.label"
                        class="student-dashboard__stat"
                        :class="`is-${stat.tone}`"
                    >
                        <span class="student-dashboard__stat-icon">
                            <i :class="stat.icon"></i>
                        </span>
                        <div>
                            <strong>{{ stat.value }}</strong>
                            <span>{{ stat.label }}</span>
                        </div>
                    </article>
                </section>

                <section class="student-dashboard__panel student-dashboard__panel--full student-dashboard__summary-panel">
                    <div class="student-dashboard__summary-layout">
                        <div class="student-dashboard__summary-card">
                            <div>
                                <span class="student-dashboard__eyebrow">Overall progress</span>
                                <h2>{{ overallProgress }}% average completion</h2>
                                <p>
                                    This average reflects all
                                    <strong class="student-dashboard__inline-strong">{{ totalCourseCount }}</strong>
                                    {{ totalCourseCount === 1 ? 'enrolled course' : 'enrolled courses' }} together, so
                                    you can quickly understand your total learning momentum.
                                </p>
                            </div>

                            <div class="student-dashboard__summary-visual">
                                <div class="student-dashboard__summary-glow"></div>
                                <div class="student-dashboard__donut-chart student-dashboard__donut-chart--summary">
                                    <DoughnutChart :data="overallAverageChartData" :options="progressChartOptions" />
                                    <div class="student-dashboard__donut-value">
                                        <strong>{{ overallProgress }}%</strong>
                                        <span>Overall</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="student-dashboard__summary-metrics">
                            <article v-for="metric in overallMetrics" :key="metric.label">
                                <strong>{{ metric.value }}</strong>
                                <span>{{ metric.label }}</span>
                                <small>{{ metric.note }}</small>
                            </article>
                        </div>
                    </div>
                </section>

                <section class="student-dashboard__content-grid">
                    <div class="student-dashboard__panel student-dashboard__panel--full">
                        <div class="student-dashboard__panel-head">
                            <div>
                                <span class="student-dashboard__eyebrow">Progress overview</span>
                                <h2>Course Progress</h2>
                            </div>
                            <span class="student-dashboard__chart-badge">{{ displayedProgressTabs.length }} latest course{{ displayedProgressTabs.length === 1 ? '' : 's' }}</span>
                        </div>

                        <div v-if="progressTabs.length" class="student-dashboard__progress-layout">
                            <article class="student-dashboard__course-spotlight">
                                <div class="student-dashboard__course-spotlight-card">
                                    <div class="student-dashboard__course-spotlight-head">
                                        <div class="student-dashboard__course-spotlight-copy">
                                            <span class="student-dashboard__status" :class="`is-${selectedProgressCourse?.statusTone || 'progress'}`">
                                                {{ selectedProgressCourse?.status || 'Active' }}
                                            </span>
                                            <h3>{{ selectedProgressCourse?.title || 'Selected course' }}</h3>
                                        </div>
                                        <small>{{ selectedCourseProgress }}% completed</small>
                                    </div>

                                    <div class="student-dashboard__donut-card student-dashboard__donut-card--course">
                                        <div class="student-dashboard__donut-chart student-dashboard__donut-chart--focus">
                                            <DoughnutChart :data="selectedProgressChartData" :options="progressChartOptions" />
                                            <div class="student-dashboard__donut-value">
                                                <strong>{{ selectedCourseProgress }}%</strong>
                                                <span>Completed</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </article>

                            <div class="student-dashboard__progress-breakdown">
                                <div class="student-dashboard__progress-list-card">
                                    <div class="student-dashboard__progress-list-head">
                                        <span class="student-dashboard__eyebrow">Latest courses</span>
                                        <small>Select a course to preview its progress chart.</small>
                                    </div>

                                    <div class="student-dashboard__progress-list" role="tablist" aria-label="Course progress list">
                                    <button
                                        v-for="(course, index) in displayedProgressTabs"
                                        :key="course.title"
                                        type="button"
                                        class="student-dashboard__progress-course"
                                        :class="{ 'is-active': index === activeDisplayedProgressIndex }"
                                        :aria-selected="index === activeDisplayedProgressIndex"
                                        role="tab"
                                        @click="selectedProgressCourseIndex = index"
                                    >
                                        <div class="student-dashboard__progress-course-top">
                                            <div class="student-dashboard__progress-course-main">
                                                <span class="student-dashboard__progress-course-index">{{ index + 1 }}</span>
                                                <strong>{{ course.title }}</strong>
                                            </div>
                                            <div class="student-dashboard__progress-course-track">
                                                <div class="student-dashboard__breakdown-bar" :title="`${course.title}: ${Math.round(Number(course.progress || 0))}% completed`">
                                                    <span :style="{ width: `${Math.round(Number(course.progress || 0))}%` }"></span>
                                                </div>
                                                <span class="student-dashboard__progress-course-percent">{{ Math.round(Number(course.progress || 0)) }}%</span>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                                </div>
                            </div>
                        </div>

                        <div v-else class="student-dashboard__empty-state">
                            <span>
                                <i class="ri-pie-chart-2-line"></i>
                            </span>
                            <div>
                                <strong>No course progress yet</strong>
                                <p>Your enrolled course progress will appear here once learning starts.</p>
                            </div>
                        </div>
                    </div>

                    <div class="student-dashboard__panel student-dashboard__panel--full">
                        <div class="student-dashboard__panel-head">
                            <div>
                                <span class="student-dashboard__eyebrow">Current courses</span>
                                <h2>My Enrolled Courses</h2>
                            </div>
                            <a v-if="courses.length" href="/student/enrolled-courses">View all</a>
                        </div>

                        <div v-if="courses.length" class="student-dashboard__table-wrap">
                            <table class="student-dashboard__table">
                                <thead>
                                    <tr>
                                        <th>Course Name</th>
                                        <th>Progress</th>
                                        <th>Status</th>
                                        <th class="text-end">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="course in courses" :key="course.title">
                                        <td>
                                            <strong>{{ course.title }}</strong>
                                        </td>
                                        <td>
                                            <div class="student-dashboard__progress" :title="`${course.title}: ${course.progress}% completed`" :aria-label="`${course.title}: ${course.progress}% completed`">
                                                <span :style="{ width: `${course.progress}%` }"></span>
                                            </div>
                                            <small>{{ course.progress }}%</small>
                                        </td>
                                        <td>
                                            <span class="student-dashboard__status" :class="`is-${course.statusTone}`">
                                                {{ course.status }}
                                            </span>
                                        </td>
                                        <td class="text-end">
                                            <button
                                                type="button"
                                                class="student-dashboard__continue"
                                                :class="{ 'is-disabled': course.isExpired }"
                                                @click="handleCourseAction(course)"
                                            >
                                                {{ course.actionLabel || 'Continue' }}
                                                <i class="ri-arrow-right-line"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else class="student-dashboard__empty-state">
                            <span>
                                <i class="ri-book-open-line"></i>
                            </span>
                            <div>
                                <strong>Please enroll in a course first.</strong>
                                <p>Once you enroll, your active courses, progress, and next learning steps will appear here.</p>
                            </div>
                            <a href="/online-training" class="student-dashboard__continue">
                                Browse Courses
                                <i class="ri-arrow-right-line"></i>
                            </a>
                        </div>
                    </div>

                    <div class="student-dashboard__panel student-dashboard__panel--full">
                        <div class="student-dashboard__panel-head">
                            <div>
                                <span class="student-dashboard__eyebrow">Activity</span>
                                <h2>Upcoming Tasks</h2>
                            </div>
                        </div>

                        <div v-if="upcomingTasks.length" class="student-dashboard__task-list student-dashboard__task-list--grid">
                            <article v-for="task in upcomingTasks" :key="`${task.typeLabel}-${task.id}`" class="student-dashboard__task">
                                <div class="student-dashboard__task-meta">
                                    <span class="student-dashboard__task-type" :class="`is-${task.statusTone || 'active'}`">
                                        {{ task.typeLabel }}
                                    </span>
                                    <small>{{ task.statusLabel }}</small>
                                </div>

                                <div class="student-dashboard__task-body">
                                    <strong>{{ task.title }}</strong>
                                    <span>{{ task.course }}</span>
                                </div>

                                <div class="student-dashboard__task-foot">
                                    <small>{{ task.due }}</small>
                                    <button
                                        type="button"
                                        class="student-dashboard__task-link"
                                        :class="{ 'is-locked': task.actionEnabled === false }"
                                        @click="handleUpcomingTaskAction($event, task)"
                                    >
                                        {{ task.actionEnabled === false ? 'Open' : 'Open' }}
                                        <i :class="task.actionEnabled === false ? 'ri-lock-line' : 'ri-arrow-right-up-line'"></i>
                                    </button>
                                </div>
                            </article>
                        </div>

                        <div v-else class="student-dashboard__empty-state">
                            <span>
                                <i class="ri-calendar-check-line"></i>
                            </span>
                            <div>
                                <strong>No upcoming tasks right now.</strong>
                                <p>Your next exam, quiz, or assignment will appear here automatically.</p>
                            </div>
                        </div>
                    </div>

                </section>
            </main>
        </div>

        <div v-if="pendingAssessment" class="student-dashboard__confirm" role="dialog" aria-modal="true">
            <div class="student-dashboard__confirm-card">
                <button type="button" class="student-dashboard__confirm-close" aria-label="Close" @click="cancelStartAssessment">
                    <i class="ri-close-line"></i>
                </button>

                <span class="student-dashboard__confirm-icon">
                    <i class="ri-timer-flash-line"></i>
                </span>

                <h2>Start {{ pendingAssessmentType }}?</h2>
                <p>
                    Do you want to start this {{ pendingAssessmentType.toLowerCase() }} now?
                    Once it starts, the timer will run and you cannot skip it.
                </p>

                <div class="student-dashboard__confirm-actions">
                    <button type="button" class="is-secondary" @click="cancelStartAssessment">Cancel</button>
                    <button type="button" class="is-primary" @click="confirmStartAssessment">
                        Yes, Start {{ pendingAssessmentType }}
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import StudentSidebar from '@/components/website/student/StudentSidebar.vue';

const root = document.getElementById('studentDashboardComponent');

const parseJson = (value, fallback = {}) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
};

const student = parseJson(root?.dataset.student);
const stats = parseJson(root?.dataset.stats, []);
const progressCourses = parseJson(root?.dataset.progressCourses, []);
const courses = parseJson(root?.dataset.courses, []);
const upcomingTasks = parseJson(root?.dataset.upcomingTasks, []);
const selectedProgressCourseIndex = ref(0);
const pendingAssessment = ref(null);

const fullName = computed(() => {
    const name = [student.name, student.last_name].filter(Boolean).join(' ').trim();
    return name || 'Student';
});

const todayLabel = computed(() => {
    return new Intl.DateTimeFormat('en', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
    }).format(new Date());
});

const overallProgress = computed(() => {
    if (!courses.length) {
        return 0;
    }

    const total = courses.reduce((sum, course) => sum + course.progress, 0);
    return Math.round(total / courses.length);
});

const totalCourseCount = computed(() => courses.length);

const activeCourseCount = computed(() => {
    return courses.filter(course => !course?.isExpired).length;
});

const completedCourseCount = computed(() => {
    return courses.filter(course => Number(course?.progress || 0) >= 100).length;
});

const welcomeHighlights = computed(() => ([
    {
        label: 'Average progress',
        value: `${overallProgress.value}%`,
    },
    {
        label: 'Active access',
        value: activeCourseCount.value,
    },
    {
        label: 'Completed',
        value: completedCourseCount.value,
    },
]));

const overallMetrics = computed(() => ([
    {
        label: 'All enrolled courses',
        value: courses.length,
        note: courses.length ? 'Used for your average completion score.' : 'Your average will show here after enrollment.',
    },
    {
        label: 'Active course access',
        value: activeCourseCount.value,
        note: activeCourseCount.value ? 'These courses are ready to continue right now.' : 'No active access is available right now.',
    },
    {
        label: 'Completed courses',
        value: completedCourseCount.value,
        note: completedCourseCount.value ? 'Finished courses already count toward your average.' : 'Completed courses will appear here as you finish them.',
    },
]));

const progressTabs = computed(() => progressCourses);
const displayedProgressTabs = computed(() => progressTabs.value.slice(0, 3));
const activeDisplayedProgressIndex = computed(() => {
    if (!displayedProgressTabs.value.length) {
        return 0;
    }

    return Math.min(selectedProgressCourseIndex.value, displayedProgressTabs.value.length - 1);
});

const selectedProgressCourse = computed(() => {
    return displayedProgressTabs.value[activeDisplayedProgressIndex.value]
        ?? null;
});

const selectedCourseProgress = computed(() => {
    return Math.round(Number(selectedProgressCourse.value?.progress ?? 0));
});

const firstCourseUrl = computed(() => {
    const activeCourse = courses.find(course => !course?.isExpired && course?.detailUrl);
    return activeCourse?.detailUrl || '/student/enrolled-courses';
});
const pendingAssessmentType = computed(() => pendingAssessment.value?.typeLabel || 'Assessment');

function buildProgressChartData(progress) {
    const safeProgress = Math.min(Math.max(Math.round(Number(progress || 0)), 0), 100);

    return {
        labels: safeProgress >= 100 ? ['Completed'] : ['Completed', 'Remaining'],
        datasets: [
            {
                data: safeProgress >= 100
                    ? [safeProgress]
                    : [safeProgress, 100 - safeProgress],
                backgroundColor: safeProgress >= 100
                    ? ['#0d6efd']
                    : ['#0d6efd', '#dfe8f5'],
                borderColor: '#ffffff',
                borderRadius: safeProgress >= 100 ? 0 : 12,
                borderWidth: safeProgress >= 100 ? 0 : 5,
                hoverBackgroundColor: safeProgress >= 100
                    ? ['#0b5ed7']
                    : ['#0b5ed7', '#bccfe8'],
            },
        ],
    };
}

const overallAverageChartData = computed(() => buildProgressChartData(overallProgress.value));

const selectedProgressChartData = computed(() => buildProgressChartData(selectedCourseProgress.value));

const progressChartOptions = {
    cutout: '78%',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            enabled: true,
            displayColors: false,
            backgroundColor: '#0f172a',
            padding: 10,
            cornerRadius: 10,
            callbacks: {
                title: () => '',
                label: (context) => `${context.label}: ${context.parsed}%`,
            },
        },
    },
};

function handleCourseAction(course) {
    if (!course?.isExpired && course?.detailUrl) {
        window.location.href = course.detailUrl;
        return;
    }

    if (course?.isExpired && window.Swal) {
        window.Swal.fire({
            icon: 'warning',
            title: 'Course Expired',
            text: course.actionMessage || 'Your course access period has ended. Please enroll again to continue learning.',
            confirmButtonText: 'Re-enroll Now',
            confirmButtonColor: '#0d6efd',
        }).then((result) => {
            if (result.isConfirmed) {
                window.location.href = course.purchaseUrl || '/online-training';
            }
        });

        return;
    }

    if (course?.isExpired) {
        window.alert(course.actionMessage || 'Your course access period has ended. Please enroll again to continue learning.');
    }
}

function needsStartConfirmation(task) {
    const actionLabel = String(task?.actionLabel || '').toLowerCase();
    const status = String(task?.status || '').toLowerCase();
    const type = String(task?.type || '').toLowerCase();

    return ['exam', 'quiz'].includes(type)
        && Boolean(task?.actionUrl)
        && actionLabel.startsWith('start')
        && ['available', 'today'].includes(status);
}

function handleUpcomingTaskAction(event, task) {
    if (task?.actionEnabled === false) {
        event?.preventDefault?.();

        const message = task?.actionMessage || 'Please first complete your lessons.';
        if (typeof window.$toast === 'function') {
            window.$toast(message, 'warning');
            return;
        }

        window.alert(message);
        return;
    }

    if (!needsStartConfirmation(task)) {
        if (task?.actionUrl) {
            window.location.href = task.actionUrl;
        }
        return;
    }

    event.preventDefault();
    pendingAssessment.value = task;
}

function cancelStartAssessment() {
    pendingAssessment.value = null;
}

function confirmStartAssessment() {
    const url = pendingAssessment.value?.actionUrl;
    pendingAssessment.value = null;

    if (url) {
        window.location.href = url;
    }
}

</script>

<style scoped>
.student-dashboard {
    background:
        radial-gradient(circle at top left, rgba(13, 110, 253, 0.11), transparent 34%),
        linear-gradient(180deg, #f4f8ff 0%, #eef1f4 48%, #f8fafc 100%);
    padding: 32px 0 46px;
}

.student-dashboard__container {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    align-items: start;
}

.student-dashboard__topbar,
.student-dashboard__welcome,
.student-dashboard__stat,
.student-dashboard__panel {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.07);
}

.student-dashboard__icon-btn {
    display: grid;
    place-items: center;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    background: #ffffff;
    color: #64748b;
    transition: all 0.2s ease;
}

.student-dashboard__icon-btn:hover {
    border-color: #6d5dfc;
    color: #6d5dfc;
}

.student-dashboard__main {
    min-width: 0;
}

.student-dashboard__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    margin-bottom: 18px;
}

.student-dashboard__topbar h1,
.student-dashboard__welcome h2,
.student-dashboard__panel-head h2 {
    margin: 0;
    color: #111827;
    font-weight: 700;
}

.student-dashboard__topbar h1 {
    font-size: 24px;
}

.student-dashboard__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
}

.student-dashboard__actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.student-dashboard__icon-btn {
    width: 42px;
    height: 42px;
    font-size: 20px;
}

.student-dashboard__primary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 42px;
    padding: 10px 16px;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 13px;
    font-weight: 800;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.student-dashboard__primary-action:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.student-dashboard__welcome {
    display: flex;
    justify-content: space-between;
    gap: 18px;
    padding: 22px;
    margin-bottom: 18px;
    background:
        linear-gradient(135deg, rgba(13, 110, 253, 0.12), rgba(47, 125, 90, 0.1)),
        #ffffff;
    border-color: #ffffff;
}

.student-dashboard__welcome h2 {
    font-size: 22px;
    line-height: 1.35;
}

.student-dashboard__welcome p {
    max-width: 720px;
    margin: 8px 0 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.7;
}

.student-dashboard__welcome-highlights {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    min-width: min(100%, 360px);
}

.student-dashboard__welcome-highlights article {
    display: grid;
    gap: 4px;
    min-height: 96px;
    padding: 16px 14px;
    border: 1px solid #ffffff;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(10px);
}

.student-dashboard__welcome-highlights strong {
    color: #111827;
    font-size: 28px;
    font-weight: 800;
    line-height: 1;
}

.student-dashboard__welcome-highlights small {
    color: #3c6fae;
    font-size: 12px;
    font-weight: 700;
}

.student-dashboard__stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 18px;
}

.student-dashboard__stat {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 104px;
    padding: 18px;
    transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.student-dashboard__stat:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 46px rgba(13, 110, 253, 0.1);
}

.student-dashboard__stat-icon {
    display: grid;
    place-items: center;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border: 1px solid currentColor;
    border-radius: 6px;
    font-size: 22px;
}

.student-dashboard__stat strong {
    display: block;
    color: #111827;
    font-size: 28px;
    line-height: 1;
}

.student-dashboard__stat span:not(.student-dashboard__stat-icon) {
    display: block;
    margin-top: 6px;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
}

.student-dashboard__stat.is-orange {
    color: #f97316;
}

.student-dashboard__stat.is-blue {
    color: #3b82f6;
}

.student-dashboard__stat.is-violet {
    color: #7c3aed;
}

.student-dashboard__stat.is-indigo {
    color: #6366f1;
}

.student-dashboard__stat.is-green {
    color: #10b981;
}

.student-dashboard__stat.is-red {
    color: #ef4444;
}

.student-dashboard__stat.is-amber {
    color: #d97706;
}

.student-dashboard__stat.is-cyan {
    color: #0891b2;
}

.student-dashboard__content-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
}

.student-dashboard__panel {
    padding: 18px;
    min-width: 0;
}

.student-dashboard__panel--wide {
    grid-column: span 1;
}

.student-dashboard__panel--full {
    grid-column: 1 / -1;
}

.student-dashboard__summary-panel {
    margin-bottom: 18px;
    overflow: hidden;
}

.student-dashboard__summary-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(280px, 0.95fr);
    gap: 18px;
}

.student-dashboard__summary-card {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 18px;
    align-items: center;
    min-height: 290px;
    padding: 24px;
    border: 1px solid #dcecff;
    border-radius: 8px;
    background:
        radial-gradient(circle at top left, rgba(13, 110, 253, 0.12), transparent 34%),
        linear-gradient(135deg, rgba(231, 241, 255, 0.92), rgba(232, 250, 243, 0.92)),
        #ffffff;
}

.student-dashboard__summary-card h2 {
    margin: 0;
    color: #111827;
    font-size: 30px;
    font-weight: 800;
    line-height: 1.2;
}

.student-dashboard__summary-card p {
    max-width: 460px;
    margin: 12px 0 0;
    color: #4b5563;
    font-size: 14px;
    line-height: 1.7;
}

.student-dashboard__inline-strong {
    color: #0f172a;
    font-weight: 900;
}

.student-dashboard__summary-visual {
    position: relative;
    display: grid;
    place-items: center;
    min-height: 240px;
}

.student-dashboard__summary-glow {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 999px;
    background:
        radial-gradient(circle, rgba(94, 234, 212, 0.26) 0%, rgba(255, 255, 255, 0) 60%),
        conic-gradient(from 180deg, rgba(13, 110, 253, 0.16), rgba(16, 185, 129, 0.18), rgba(245, 158, 11, 0.14), rgba(13, 110, 253, 0.16));
    filter: blur(18px);
    opacity: 0.9;
}

.student-dashboard__summary-metrics {
    display: grid;
    gap: 14px;
}

.student-dashboard__summary-metrics article {
    display: grid;
    gap: 4px;
    min-height: 86px;
    padding: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.student-dashboard__summary-metrics strong {
    color: #111827;
    font-size: 28px;
    font-weight: 800;
    line-height: 1;
}

.student-dashboard__summary-metrics span {
    color: #0d6efd;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
}

.student-dashboard__summary-metrics small {
    color: #64748b;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.55;
}

.student-dashboard__chart-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
    padding: 6px 12px;
    border-radius: 999px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
}

.student-dashboard__progress-layout {
    display: grid;
    grid-template-columns: minmax(340px, 0.95fr) minmax(0, 1.05fr);
    gap: 20px;
    align-items: start;
}

.student-dashboard__donut-card {
    display: grid;
    place-items: center;
    align-content: center;
    min-height: 310px;
    padding: 22px 18px;
    border: 1px solid #edf2f7;
    border-radius: 8px;
    background:
        radial-gradient(circle at center, rgba(13, 110, 253, 0.08), transparent 62%),
        linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.student-dashboard__donut-chart {
    position: relative;
    width: min(210px, 100%);
    aspect-ratio: 1;
}

.student-dashboard__donut-chart--summary {
    width: min(236px, 100%);
}

.student-dashboard__donut-chart--focus {
    width: min(238px, 100%);
}

.student-dashboard__donut-value {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    pointer-events: none;
    text-align: center;
}

.student-dashboard__donut-value strong {
    color: #111827;
    font-size: 38px;
    font-weight: 800;
    line-height: 1;
}

.student-dashboard__donut-value span {
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.student-dashboard__course-spotlight-card {
    display: grid;
    gap: 18px;
    height: 100%;
    padding: 22px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background:
        radial-gradient(circle at top right, rgba(13, 110, 253, 0.1), transparent 28%),
        linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
    align-content: center;
    box-shadow: inset 0 0 0 1px rgba(13, 110, 253, 0.04);
}

.student-dashboard__course-spotlight-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
}

.student-dashboard__course-spotlight-copy {
    display: grid;
    gap: 10px;
}

.student-dashboard__course-spotlight-copy h3 {
    margin: 0;
    color: #0f172a;
    font-size: 24px;
    font-weight: 800;
    line-height: 1.3;
}

.student-dashboard__course-spotlight-head small {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    padding-top: 8px;
}

.student-dashboard__donut-card--course {
    min-height: 320px;
    border-color: #dbe7fb;
    background:
        radial-gradient(circle at center, rgba(13, 110, 253, 0.12), transparent 58%),
        linear-gradient(180deg, #ffffff 0%, #f6faff 100%);
}

.student-dashboard__progress-breakdown {
    display: grid;
    gap: 12px;
    align-content: start;
}

.student-dashboard__progress-list-card {
    display: grid;
    gap: 16px;
    height: auto;
    padding: 20px;
    border: 1px solid #dbe7fb;
    border-radius: 18px;
    background:
        radial-gradient(circle at top right, rgba(13, 110, 253, 0.07), transparent 30%),
        linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
    box-shadow: inset 0 0 0 1px rgba(13, 110, 253, 0.04);
}

.student-dashboard__progress-list-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.student-dashboard__progress-list-head small {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.6;
    text-align: right;
}

.student-dashboard__progress-list {
    display: grid;
    gap: 10px;
    height: auto;
}

.student-dashboard__progress-course {
    display: block;
    width: 100%;
    min-height: 0;
    padding: 14px 12px;
    border: 1px solid rgba(219, 231, 251, 0.72);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.76);
    text-align: left;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.student-dashboard__progress-course:hover {
    transform: none;
    border-color: rgba(147, 197, 253, 0.9);
    background: linear-gradient(180deg, rgba(248, 251, 255, 0.96) 0%, rgba(241, 247, 255, 0.96) 100%);
    box-shadow: 0 10px 22px rgba(13, 110, 253, 0.08);
}

.student-dashboard__progress-course.is-active {
    padding: 14px 12px;
    border-radius: 14px;
    border-color: rgba(96, 165, 250, 0.95);
    background:
        radial-gradient(circle at top right, rgba(13, 110, 253, 0.08), transparent 32%),
        linear-gradient(180deg, #f6faff 0%, #edf5ff 100%);
    box-shadow:
        inset 0 0 0 1px rgba(147, 197, 253, 0.8),
        0 12px 24px rgba(13, 110, 253, 0.1);
}

.student-dashboard__progress-course-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
}

.student-dashboard__progress-course-main {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1;
}

.student-dashboard__progress-course-track {
    display: flex;
    align-items: center;
    gap: 12px;
    width: min(250px, 48%);
    flex-shrink: 0;
}

.student-dashboard__progress-course-index,
.student-dashboard__progress-course-percent {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
}

.student-dashboard__progress-course-index {
    min-width: 36px;
    padding: 4px 10px;
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    color: #2563eb;
    flex-shrink: 0;
}

.student-dashboard__progress-course-percent {
    min-width: 36px;
    color: #475569;
}

.student-dashboard__progress-breakdown strong {
    color: #111827;
    font-size: 15px;
    font-weight: 800;
    line-height: 1.4;
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
}

.student-dashboard__breakdown-bar {
    position: relative;
    height: 10px;
    flex: 1;
    overflow: hidden;
    border-radius: 999px;
    background: linear-gradient(90deg, #e8f0ff 0%, #eef4ff 100%);
    box-shadow: inset 0 1px 2px rgba(148, 163, 184, 0.18);
}

.student-dashboard__breakdown-bar span {
    position: relative;
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #0d6efd 0%, #20c997 55%, #7c3aed 100%);
    box-shadow: 0 6px 14px rgba(13, 110, 253, 0.24);
    transform-origin: left;
    animation: studentProgressGrow 1.15s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.student-dashboard__breakdown-bar span::after,
.student-dashboard__progress span::after {
    position: absolute;
    inset: 0;
    content: "";
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.48), transparent);
    animation: studentProgressShine 2.2s ease-in-out infinite;
}

.student-dashboard__panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.student-dashboard__panel-head h2 {
    font-size: 18px;
}

.student-dashboard__panel-head a {
    color: #5b4ef5;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none;
    white-space: nowrap;
}

.student-dashboard__table-wrap {
    overflow-x: auto;
}

.student-dashboard__empty-state {
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) auto;
    align-items: center;
    gap: 14px;
    min-height: 112px;
    padding: 18px;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    background: #f8fbff;
}

.student-dashboard__empty-state > span {
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 24px;
}

.student-dashboard__empty-state strong,
.student-dashboard__empty-state p {
    display: block;
}

.student-dashboard__empty-state strong {
    color: #111827;
    font-size: 15px;
    font-weight: 800;
}

.student-dashboard__empty-state p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
}

.student-dashboard__table {
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;
}

.student-dashboard__table th,
.student-dashboard__table td {
    padding: 13px 12px;
    border-bottom: 1px solid #edf2f7;
    text-align: left;
    vertical-align: middle;
}

.student-dashboard__table th {
    background: #f8fafc;
    color: #475569;
    font-size: 12px;
    font-weight: 800;
}

.student-dashboard__table td {
    color: #334155;
    font-size: 13px;
}

.student-dashboard__table td strong {
    display: block;
}

.student-dashboard__table td strong {
    color: #111827;
    font-weight: 700;
}

.student-dashboard__progress {
    position: relative;
    width: 136px;
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: #e5e7eb;
}

.student-dashboard__progress span {
    position: relative;
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #0d6efd 0%, #20c997 60%, #7c3aed 100%);
    transform-origin: left;
    animation: studentProgressGrow 1.15s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.student-dashboard__table small {
    display: block;
    margin-top: 5px;
    color: #64748b;
    font-size: 12px;
}

.student-dashboard__rating {
    display: inline-flex;
    gap: 2px;
    color: #f59e0b;
    font-size: 15px;
}

.student-dashboard__status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 92px;
    min-height: 28px;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
}

.student-dashboard__status.is-progress {
    background: #eff6ff;
    color: #2563eb;
}

.student-dashboard__status.is-completed {
    background: #ecfdf5;
    color: #047857;
}

.student-dashboard__status.is-continue {
    background: #eff6ff;
    color: #2563eb;
}

.student-dashboard__status.is-expired {
    background: #fef2f2;
    color: #dc2626;
}

.student-dashboard__continue {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 34px;
    padding: 7px 11px;
    border-radius: 8px;
    border: 0;
    background: #e7f1ff;
    color: #0d6efd;
    cursor: pointer;
    font-size: 12px;
    font-weight: 900;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
    white-space: nowrap;
}

.student-dashboard__continue:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.student-dashboard__continue.is-disabled {
    background: #fef2f2;
    color: #dc2626;
}

.text-end {
    text-align: right !important;
}

.student-dashboard__task-list,
.student-dashboard__skill-grid {
    display: grid;
    gap: 12px;
}

.student-dashboard__task,
.student-dashboard__skill {
    padding: 16px;
    border: 1px solid #e7eef8;
    border-radius: 8px;
    background:
        linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(244, 249, 255, 0.95)),
        #ffffff;
}

.student-dashboard__task {
    display: grid;
    gap: 14px;
    min-height: 178px;
    box-shadow: 0 18px 36px rgba(13, 110, 253, 0.06);
}

.student-dashboard__task strong,
.student-dashboard__skill strong {
    display: block;
    color: #111827;
    font-size: 16px;
    font-weight: 800;
}

.student-dashboard__task-meta,
.student-dashboard__task-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.student-dashboard__task-type {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.01em;
    text-transform: uppercase;
}

.student-dashboard__task-type.is-active {
    background: #e0f2fe;
    color: #0369a1;
}

.student-dashboard__task-type.is-today {
    background: #dbeafe;
    color: #1d4ed8;
}

.student-dashboard__task-type.is-upcoming {
    background: #eff6ff;
    color: #2563eb;
}

.student-dashboard__task-type.is-locked {
    background: #fff7ed;
    color: #c2410c;
}

.student-dashboard__task-body span,
.student-dashboard__task-meta small,
.student-dashboard__task-foot small {
    display: block;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.5;
}

.student-dashboard__task-list--grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.student-dashboard__task-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 0;
    background: transparent;
    color: #0d6efd;
    font-size: 12px;
    font-weight: 800;
    text-decoration: none;
    cursor: pointer;
}

.student-dashboard__task-link.is-locked {
    color: #c2410c;
}

.student-dashboard__confirm {
    position: fixed;
    inset: 0;
    z-index: 60;
    display: grid;
    place-items: center;
    padding: 16px;
    background: rgba(15, 23, 42, 0.56);
}

.student-dashboard__confirm-card {
    position: relative;
    width: min(100%, 420px);
    padding: 28px 24px 24px;
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 28px 64px rgba(15, 23, 42, 0.24);
    text-align: center;
}

.student-dashboard__confirm-close {
    position: absolute;
    top: 14px;
    right: 14px;
    border: 0;
    background: transparent;
    color: #64748b;
    font-size: 22px;
}

.student-dashboard__confirm-icon {
    display: inline-grid;
    place-items: center;
    width: 70px;
    height: 70px;
    margin-bottom: 18px;
    border-radius: 999px;
    background: linear-gradient(135deg, #e0ecff, #dbeafe);
    color: #0d6efd;
    font-size: 30px;
}

.student-dashboard__confirm-card h2 {
    margin: 0 0 10px;
    color: #111827;
    font-size: 24px;
    font-weight: 800;
}

.student-dashboard__confirm-card p {
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.7;
}

.student-dashboard__confirm-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 22px;
}

.student-dashboard__confirm-actions button {
    min-width: 142px;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 999px;
    border: 0;
    font-size: 13px;
    font-weight: 800;
}

.student-dashboard__confirm-actions .is-secondary {
    background: #eef2f7;
    color: #475569;
}

.student-dashboard__confirm-actions .is-primary {
    background: #0d6efd;
    color: #ffffff;
}

.student-dashboard__skill-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

@keyframes studentProgressGrow {
    from {
        transform: scaleX(0);
    }
    to {
        transform: scaleX(1);
    }
}

@keyframes studentProgressShine {
    0% {
        transform: translateX(-110%);
    }
    55%,
    100% {
        transform: translateX(110%);
    }
}

.student-dashboard__timeline-item > span.is-blue,
.student-dashboard__skill > span.is-blue {
    background: #eff6ff;
    color: #2563eb;
}

.student-dashboard__timeline-item > span.is-orange,
.student-dashboard__skill > span.is-orange {
    background: #fff7ed;
    color: #ea580c;
}

.student-dashboard__timeline-item > span.is-green,
.student-dashboard__skill > span.is-green {
    background: #ecfdf5;
    color: #059669;
}

.student-dashboard__timeline-item > span.is-violet,
.student-dashboard__skill > span.is-violet {
    background: #f5f3ff;
    color: #7c3aed;
}

@media (max-width: 1199px) {
    .student-dashboard__container {
        grid-template-columns: 210px minmax(0, 1fr);
    }

    .student-dashboard__stats {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .student-dashboard__summary-layout,
    .student-dashboard__progress-layout {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 991px) {
    .student-dashboard {
        padding: 24px 0 36px;
    }

    .student-dashboard__container {
        grid-template-columns: 1fr;
    }

    .student-dashboard__content-grid {
        grid-template-columns: 1fr;
    }

    .student-dashboard__task-list--grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .student-dashboard__summary-card {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 767px) {
    .student-dashboard__topbar,
    .student-dashboard__welcome {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-dashboard__welcome-highlights {
        width: 100%;
        min-width: 0;
    }

    .student-dashboard__actions {
        width: 100%;
    }

    .student-dashboard__primary-action {
        flex: 1;
    }

    .student-dashboard__donut-card {
        min-height: 280px;
    }

    .student-dashboard__empty-state {
        grid-template-columns: 1fr;
        justify-items: start;
    }

    .student-dashboard__stats,
    .student-dashboard__skill-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .student-dashboard__welcome-highlights,
    .student-dashboard__summary-metrics {
        grid-template-columns: 1fr;
    }

    .student-dashboard__panel-head {
        flex-direction: column;
        align-items: flex-start;
    }
}

@media (max-width: 575px) {
    .student-dashboard__container {
        padding-left: 12px;
        padding-right: 12px;
    }

    .student-dashboard__topbar,
    .student-dashboard__welcome,
    .student-dashboard__panel {
        padding: 16px;
    }

    .student-dashboard__stats,
    .student-dashboard__skill-grid {
        grid-template-columns: 1fr;
    }

    .student-dashboard__summary-card,
    .student-dashboard__course-spotlight-card {
        padding: 16px;
    }

    .student-dashboard__task-list--grid {
        grid-template-columns: 1fr;
    }

    .student-dashboard__stat {
        min-height: 92px;
    }

    .student-dashboard__chart-badge {
        white-space: normal;
    }
}
</style>
